// integrations.oS front door — the pre-login page, built on refractions'
// door (crucible_ofself src/landing/refractions-landing.jsx): two blocks on
// the canvas ink, cut by the ofself seam, pinned while a taller stage
// scrolls beneath them. Scrolling cross-fades what the blocks HOLD, and each
// figure is fed its phase's local progress so the scroll draws it.
//
// The story walks what this app does, in order: the mark arrives → your life
// is scattered across services → it converges into one graph → you hold the
// boundary → it comes home (Connect returns).
//
// html/body are overflow:hidden for the signed-in shell, so the door is its
// OWN scroll container (.ig-landing) — the sticky pin and the progress math
// both read that element, not the window.
import React, { useEffect, useRef } from 'react';
import { CtaButton } from '@ofself/shell';
import '@fontsource/space-grotesk/400.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource-variable/hanken-grotesk';
import { ConvergenceMark, ScatterMark, ConvergeMark, BoundaryMark } from './marks.jsx';
import './landing.css';

const N_PHASES = 5;
// half-width of each hand-off in stage progress — phase i owns [i/N, (i+1)/N]
// and trades with its neighbours across 2×FADE of it
const FADE = 0.032;
const PHASE_WINDOWS = Array.from({ length: N_PHASES }, (_, i) => [
  i === 0 ? -1 : i / N_PHASES - FADE,
  i === 0 ? -1 : i / N_PHASES + FADE,
  i === N_PHASES - 1 ? 2 : (i + 1) / N_PHASES - FADE,
  i === N_PHASES - 1 ? 2 : (i + 1) / N_PHASES + FADE,
]);
function phaseOpacity(p, idx) {
  const [inA, inB, outA, outB] = PHASE_WINDOWS[idx];
  const rise = p <= inA ? 0 : p >= inB ? 1 : (p - inA) / (inB - inA);
  const fall = p <= outA ? 0 : p >= outB ? 1 : (p - outA) / (outB - outA);
  const s = (t) => t * t * (3 - 2 * t);
  return s(rise) * (1 - s(fall));
}

const CONNECT_HREF = '/auth/connect';

export default function Landing({ message }) {
  const scrollerRef = useRef(null);
  const stageRef = useRef(null);
  const layerRefs = useRef(PHASE_WINDOWS.map(() => []));
  const setLayer = (phase, slot) => (el) => { layerRefs.current[phase][slot] = el; };
  const counterRef = useRef(null);
  const hintRef = useRef(null);
  // per-phase local progress, written by the scroll, read by the figures
  const scatterProgress = useRef(0);
  const convergeProgress = useRef(0);
  const boundaryProgress = useRef(0);
  const homeProgress = useRef(0);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const stage = stageRef.current;
    if (!scroller || !stage) return undefined;
    let raf = 0;
    let lastActive = -1;
    const apply = () => {
      raf = 0;
      const span = stage.offsetHeight - scroller.clientHeight;
      const p = span > 0 ? Math.min(1, Math.max(0, scroller.scrollTop / span)) : 0;
      const local = (i) => Math.min(1, Math.max(0, p * N_PHASES - i));
      scatterProgress.current = local(1);
      convergeProgress.current = local(2);
      boundaryProgress.current = local(3);
      homeProgress.current = local(4);
      let active = 0, best = -1;
      layerRefs.current.forEach((els, idx) => {
        const o = phaseOpacity(p, idx);
        if (o > best) { best = o; active = idx; }
        // incoming rises to its seat; outgoing drifts on upward
        const dy = (1 - o) * 18 * (p < (idx + 0.5) / N_PHASES ? 1 : -1);
        for (const el of els) {
          if (!el) continue;
          el.style.opacity = o.toFixed(3);
          el.style.transform = `translateY(${dy.toFixed(1)}px)`;
          el.style.pointerEvents = o > 0.5 ? '' : 'none';
          el.setAttribute('aria-hidden', o > 0.5 ? 'false' : 'true');
        }
      });
      if (active !== lastActive && counterRef.current) {
        lastActive = active;
        counterRef.current.textContent = `0${active + 1} / 0${N_PHASES}`;
      }
      if (hintRef.current) hintRef.current.style.opacity = Math.max(0, 1 - p * 25).toFixed(3);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(apply); };
    apply();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div className="ig-landing" ref={scrollerRef}>
      <div className="ig-doorstage" ref={stageRef}>
        <div className="ig-door">
          <section className="ig-door__left">
            <div className="ig-door__wordmark">integrations<span>.oS</span></div>
            <div className="ig-door__pitch ig-door__stack">
              <div className="ig-door__layer" ref={setLayer(0, 0)}>
                {message ? <p className="ig-door__msg" role="status">{message}</p> : null}
                <h1 className="ig-door__tag">I gather your digital life into your identity</h1>
                <CtaButton href={CONNECT_HREF}>Connect</CtaButton>
              </div>
              <div className="ig-door__layer" ref={setLayer(1, 0)}>
                <h2 className="ig-door__tag">Your life is scattered</h2>
                <p className="ig-door__sub">
                  Your mail, your calendar, your code, your music, your games,
                  your heartbeat — each held by a different service, each
                  seeing one sliver of you. None of them sees the whole.
                </p>
              </div>
              <div className="ig-door__layer" ref={setLayer(2, 0)}>
                <h2 className="ig-door__tag">I draw it into one graph</h2>
                <p className="ig-door__sub">
                  Each source I connect is read by a plugin that writes what
                  it finds into your identity graph — people, events,
                  repositories, songs, games, nights of sleep. The scattered
                  traces converge into a single picture of you.
                </p>
              </div>
              <div className="ig-door__layer" ref={setLayer(3, 0)}>
                <h2 className="ig-door__tag">You hold every door</h2>
                <p className="ig-door__sub">
                  A source connects only when you connect it, and reads only
                  what you grant. Everything is read-only unless you choose to
                  edit, and any door you open, you can close.
                </p>
              </div>
              <div className="ig-door__layer" ref={setLayer(4, 0)}>
                <h2 className="ig-door__tag">Your data comes home</h2>
                <p className="ig-door__sub">
                  It lives in your own graph, under your own identity, through
                  Paradigm — sovereign, and yours to take anywhere.
                </p>
                <CtaButton href={CONNECT_HREF}>Connect</CtaButton>
              </div>
            </div>
            <div className="ig-door__foot">
              <span ref={counterRef} className="ig-door__count">01 / 05</span>
              <span ref={hintRef} className="ig-door__hint" aria-hidden="true">scroll ↓</span>
            </div>
          </section>
          <section className="ig-door__right" aria-hidden="true">
            <div className="ig-door__stack ig-door__stack--fill">
              <div className="ig-door__layer" ref={setLayer(0, 1)}><ConvergenceMark /></div>
              <div className="ig-door__layer" ref={setLayer(1, 1)}><ScatterMark progressRef={scatterProgress} /></div>
              <div className="ig-door__layer" ref={setLayer(2, 1)}><ConvergeMark progressRef={convergeProgress} /></div>
              <div className="ig-door__layer" ref={setLayer(3, 1)}><BoundaryMark progressRef={boundaryProgress} /></div>
              <div className="ig-door__layer" ref={setLayer(4, 1)}><ConvergenceMark nucleusRef={homeProgress} /></div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
